/**
 * XR Scene Controls
 *
 * Provides orbit/drag camera controls for XR emulator and desktop debugging.
 * The camera moves around the scene; the reading pane remains world-locked.
 *
 * Controls:
 * - Left mouse drag: orbit around target
 * - Right mouse drag / two-finger drag: pan
 * - Scroll wheel / pinch: zoom in/out
 *
 * Constitution Article XII compliance:
 * - Camera controls move the camera only
 * - Reading pane position is never modified
 */

import * as THREE from 'three';

/**
 * Scene controls configuration
 */
interface SceneControlsConfig {
  /** Target point to orbit around (reading pane center) */
  target: THREE.Vector3;
  /** Minimum distance from target */
  minDistance: number;
  /** Maximum distance from target */
  maxDistance: number;
  /** Rotation speed multiplier */
  rotateSpeed: number;
  /** Pan speed multiplier */
  panSpeed: number;
  /** Zoom speed multiplier */
  zoomSpeed: number;
}

const DEFAULT_CONFIG: SceneControlsConfig = {
  target: new THREE.Vector3(0, 1.6, -1.2), // Reading pane center
  minDistance: 0.5,
  maxDistance: 5.0,
  rotateSpeed: 0.005,
  panSpeed: 0.002,
  zoomSpeed: 0.001,
};

/**
 * XR Scene Controls for emulator/debug camera manipulation
 */
export class XRSceneControls {
  private camera: THREE.PerspectiveCamera;
  private domElement: HTMLElement;
  private config: SceneControlsConfig;

  // Spherical coordinates for orbit
  private spherical: THREE.Spherical;
  private sphericalDelta: THREE.Spherical;

  // State tracking
  private isEnabled: boolean = false;
  private isDragging: boolean = false;
  private isPanning: boolean = false;
  private isTouchOnUI: boolean = false; // Track if touch started on UI element
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;
  private lastTouchDistance: number = 0;

  // Bound event handlers for cleanup
  private boundOnMouseDown: (e: MouseEvent) => void;
  private boundOnMouseMove: (e: MouseEvent) => void;
  private boundOnMouseUp: (e: MouseEvent) => void;
  private boundOnWheel: (e: WheelEvent) => void;
  private boundOnTouchStart: (e: TouchEvent) => void;
  private boundOnTouchMove: (e: TouchEvent) => void;
  private boundOnTouchEnd: (e: TouchEvent) => void;
  private boundOnContextMenu: (e: Event) => void;

  constructor(
    camera: THREE.PerspectiveCamera,
    domElement: HTMLElement,
    config: Partial<SceneControlsConfig> = {}
  ) {
    this.camera = camera;
    this.domElement = domElement;
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Initialize spherical coordinates from current camera position
    this.spherical = new THREE.Spherical();
    this.sphericalDelta = new THREE.Spherical();
    this.updateSphericalFromCamera();

    // Bind event handlers
    this.boundOnMouseDown = this.onMouseDown.bind(this);
    this.boundOnMouseMove = this.onMouseMove.bind(this);
    this.boundOnMouseUp = this.onMouseUp.bind(this);
    this.boundOnWheel = this.onWheel.bind(this);
    this.boundOnTouchStart = this.onTouchStart.bind(this);
    this.boundOnTouchMove = this.onTouchMove.bind(this);
    this.boundOnTouchEnd = this.onTouchEnd.bind(this);
    this.boundOnContextMenu = (e: Event) => e.preventDefault();
  }

  /**
   * Update spherical coordinates from current camera position
   */
  private updateSphericalFromCamera(): void {
    const offset = new THREE.Vector3();
    offset.copy(this.camera.position).sub(this.config.target);
    this.spherical.setFromVector3(offset);
  }

  /**
   * Update camera position from spherical coordinates
   */
  private updateCameraFromSpherical(): void {
    // Apply delta
    this.spherical.theta += this.sphericalDelta.theta;
    this.spherical.phi += this.sphericalDelta.phi;

    // Clamp phi to avoid flipping
    this.spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, this.spherical.phi));

    // Clamp radius
    this.spherical.radius = Math.max(
      this.config.minDistance,
      Math.min(this.config.maxDistance, this.spherical.radius)
    );

    // Convert back to cartesian and apply to camera
    const offset = new THREE.Vector3();
    offset.setFromSpherical(this.spherical);
    this.camera.position.copy(this.config.target).add(offset);

    // Look at target
    this.camera.lookAt(this.config.target);

    // Reset delta
    this.sphericalDelta.set(0, 0, 0);
  }

  /**
   * Enable controls
   */
  enable(): void {
    if (this.isEnabled) return;

    this.domElement.addEventListener('mousedown', this.boundOnMouseDown);
    this.domElement.addEventListener('wheel', this.boundOnWheel, { passive: false });
    this.domElement.addEventListener('touchstart', this.boundOnTouchStart, { passive: false });
    this.domElement.addEventListener('contextmenu', this.boundOnContextMenu);

    // Global listeners for drag continuation
    window.addEventListener('mousemove', this.boundOnMouseMove);
    window.addEventListener('mouseup', this.boundOnMouseUp);
    window.addEventListener('touchmove', this.boundOnTouchMove, { passive: false });
    window.addEventListener('touchend', this.boundOnTouchEnd);

    this.isEnabled = true;
  }

  /**
   * Disable controls
   */
  disable(): void {
    if (!this.isEnabled) return;

    this.domElement.removeEventListener('mousedown', this.boundOnMouseDown);
    this.domElement.removeEventListener('wheel', this.boundOnWheel);
    this.domElement.removeEventListener('touchstart', this.boundOnTouchStart);
    this.domElement.removeEventListener('contextmenu', this.boundOnContextMenu);

    window.removeEventListener('mousemove', this.boundOnMouseMove);
    window.removeEventListener('mouseup', this.boundOnMouseUp);
    window.removeEventListener('touchmove', this.boundOnTouchMove);
    window.removeEventListener('touchend', this.boundOnTouchEnd);

    this.isDragging = false;
    this.isPanning = false;
    this.isEnabled = false;
  }

  /**
   * Update controls (call each frame)
   */
  update(): void {
    if (!this.isEnabled) return;
    this.updateCameraFromSpherical();
  }

  // ---------------------------------------------------------------------------
  // Mouse handlers
  // ---------------------------------------------------------------------------

  private onMouseDown(event: MouseEvent): void {
    event.preventDefault();

    if (event.button === 0) {
      // Left button: orbit
      this.isDragging = true;
      this.isPanning = false;
    } else if (event.button === 2) {
      // Right button: pan
      this.isDragging = false;
      this.isPanning = true;
    }

    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.isDragging && !this.isPanning) return;

    const deltaX = event.clientX - this.lastMouseX;
    const deltaY = event.clientY - this.lastMouseY;

    if (this.isDragging) {
      // Orbit: rotate around target
      this.sphericalDelta.theta -= deltaX * this.config.rotateSpeed;
      this.sphericalDelta.phi -= deltaY * this.config.rotateSpeed;
    } else if (this.isPanning) {
      // Pan: move target and camera together
      this.pan(deltaX, deltaY);
    }

    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;
  }

  private onMouseUp(_event: MouseEvent): void {
    this.isDragging = false;
    this.isPanning = false;
  }

  private onWheel(event: WheelEvent): void {
    event.preventDefault();

    // Zoom: adjust radius
    this.spherical.radius += event.deltaY * this.config.zoomSpeed;
  }

  // ---------------------------------------------------------------------------
  // Touch handlers
  // ---------------------------------------------------------------------------

  private onTouchStart(event: TouchEvent): void {
    // Ignore events from UI controls (sliders, buttons, etc.)
    if (this.isUIElement(event.target as Element)) {
      this.isTouchOnUI = true;
      return;
    }

    this.isTouchOnUI = false;
    event.preventDefault();

    if (event.touches.length === 1) {
      // Single touch: orbit
      this.isDragging = true;
      this.isPanning = false;
      this.lastMouseX = event.touches[0].clientX;
      this.lastMouseY = event.touches[0].clientY;
    } else if (event.touches.length === 2) {
      // Two touches: pan + pinch zoom
      this.isDragging = false;
      this.isPanning = true;
      this.lastTouchDistance = this.getTouchDistance(event.touches);
      const center = this.getTouchCenter(event.touches);
      this.lastMouseX = center.x;
      this.lastMouseY = center.y;
    }
  }

  private onTouchMove(event: TouchEvent): void {
    // Ignore if touch started on UI element
    if (this.isTouchOnUI) return;

    // Also check current target - touch may have started on UI element above canvas
    // (canvas touchstart wouldn't fire, so isTouchOnUI wouldn't be set)
    if (this.isUIElement(event.target as Element)) return;

    // Only prevent default if we're actually dragging/panning
    if (!this.isDragging && !this.isPanning) return;

    event.preventDefault();

    if (event.touches.length === 1 && this.isDragging) {
      const deltaX = event.touches[0].clientX - this.lastMouseX;
      const deltaY = event.touches[0].clientY - this.lastMouseY;

      this.sphericalDelta.theta -= deltaX * this.config.rotateSpeed;
      this.sphericalDelta.phi -= deltaY * this.config.rotateSpeed;

      this.lastMouseX = event.touches[0].clientX;
      this.lastMouseY = event.touches[0].clientY;
    } else if (event.touches.length === 2) {
      // Pinch zoom
      const distance = this.getTouchDistance(event.touches);
      const deltaDistance = this.lastTouchDistance - distance;
      this.spherical.radius += deltaDistance * this.config.zoomSpeed * 10;
      this.lastTouchDistance = distance;

      // Two-finger pan
      const center = this.getTouchCenter(event.touches);
      const deltaX = center.x - this.lastMouseX;
      const deltaY = center.y - this.lastMouseY;
      this.pan(deltaX, deltaY);
      this.lastMouseX = center.x;
      this.lastMouseY = center.y;
    }
  }

  private onTouchEnd(_event: TouchEvent): void {
    this.isDragging = false;
    this.isPanning = false;
    this.isTouchOnUI = false;
  }

  private getTouchDistance(touches: TouchList): number {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private getTouchCenter(touches: TouchList): { x: number; y: number } {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    };
  }

  // ---------------------------------------------------------------------------
  // UI detection helper
  // ---------------------------------------------------------------------------

  /**
   * Check if an element is part of the UI controls (should not trigger 3D controls)
   */
  private isUIElement(element: Element | null): boolean {
    if (!element) return false;

    // Check if element or any parent is a UI control
    let current: Element | null = element;
    while (current) {
      // Check for common UI control classes/elements
      if (
        current.classList?.contains('mobile-controls') ||
        current.classList?.contains('mobile-xr-buttons') ||
        current.classList?.contains('mobile-camera-toggle') ||
        current.classList?.contains('wpm-slider') ||
        current.classList?.contains('transport-btn') ||
        current.classList?.contains('control-btn') ||
        current.tagName === 'INPUT' ||
        current.tagName === 'BUTTON' ||
        current.tagName === 'LABEL'
      ) {
        return true;
      }
      current = current.parentElement;
    }
    return false;
  }

  // ---------------------------------------------------------------------------
  // Pan helper
  // ---------------------------------------------------------------------------

  private pan(deltaX: number, deltaY: number): void {
    // Calculate pan offset in camera space
    const offset = new THREE.Vector3();

    // Get camera's right and up vectors
    const right = new THREE.Vector3();
    const up = new THREE.Vector3();
    this.camera.matrix.extractBasis(right, up, new THREE.Vector3());

    // Apply pan
    offset.addScaledVector(right, -deltaX * this.config.panSpeed);
    offset.addScaledVector(up, deltaY * this.config.panSpeed);

    // Move target (camera follows via spherical update)
    this.config.target.add(offset);
  }

  /**
   * Reset camera to initial position
   */
  reset(): void {
    this.config.target.set(0, 1.6, -1.2);
    this.camera.position.set(0, 1.6, 0);
    this.updateSphericalFromCamera();
  }

  /**
   * Check if controls are enabled
   */
  get enabled(): boolean {
    return this.isEnabled;
  }
}

/**
 * Create XR scene controls for a camera
 */
export function createXRSceneControls(
  camera: THREE.PerspectiveCamera,
  domElement: HTMLElement,
  config?: Partial<SceneControlsConfig>
): XRSceneControls {
  return new XRSceneControls(camera, domElement, config);
}
